import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState
} from 'react'
import 'swiper/css'
import { useRouter } from 'next/router'
import {
  HeaderNotice,
  HomeBanner,
  Market,
  NavigationItem,
  PremiumManufacturer
} from '@/network/graphql.g'
import { useMainNavigationDispatch } from '@/providers/mainNavigationMenuProvider'
import { lastVisitedMainCategoryKey } from '../../../hooks/useCurrentMainCategoryId'
import MobileSubcategoriesMenuRedesign, {
  MobileSubcategoriesMenuRedesignActions
} from '@/components/organisms/mobileSubcategoriesMenuRedesign'
import MobileSubcategoryItemsMenuRedesign from '@/components/organisms/mobileSubcategoryItemRedesign'
import MainSlideMenuPagesRedesign, {
  SlideMenuActions
} from '@/components/organisms/mainSideSlideMenu/mainSlideMenuPagesRedesign'

type MainSlideMenuRedesignProps = {
  navigationItems: NavigationItem[]
  otherMenuItems: NavigationItem[]
  onDidClose?: () => void
  markets: Market[]
  premiumManufacturers: PremiumManufacturer[]
  showSlideMenu?: boolean
  setShowSlideMenu?: React.Dispatch<React.SetStateAction<boolean>>
  showHeaderPromo?: boolean
  headerNotice?: HeaderNotice
}

export type MainSlideMenuActions = {
  open: () => void
}

const MainSlideMenuRedesign = forwardRef<
  MainSlideMenuActions,
  MainSlideMenuRedesignProps
>(
  (
    {
      navigationItems,
      otherMenuItems,
      showSlideMenu,
      setShowSlideMenu,
      showHeaderPromo,
      headerNotice
    },
    ref
  ) => {
    const subcategoriesEl = useRef<MobileSubcategoriesMenuRedesignActions>(null)

    const [currentCategoryId, setCurrentCategoryId] =
      useState<string | null>(null)
    const subcategoriesItemsMenuEl =
      useRef<MobileSubcategoriesMenuRedesignActions>(null)
    const slideMenu = useRef<SlideMenuActions>(null)
    const { slugPath } = useMainNavigationDispatch()
    const [selectedSubcategory, setSelectedSubcategory] =
      useState<NavigationItem | null>(null)
    const [selectedSubcategoryBanners, setSelectedSubcategoryBanners] =
      useState<HomeBanner[]>([])

    useImperativeHandle(ref, () => ({
      open: () => setShowSlideMenu && setShowSlideMenu(true)
    }))

    const router = useRouter()
    useEffect(() => {
      setShowSlideMenu(false)
    }, [router])

    useEffect(() => {
      let timeoutId

      if (showSlideMenu) {
        const slugPathActive = slugPath.find((path) => path.id)
        const activeCategory = navigationItems
          ?.filter(({ isMainCategory }) => isMainCategory)
          .find((item) => item.categoryId === slugPathActive?.id)
        const firstCategory = navigationItems?.filter(
          ({ isMainCategory }) => isMainCategory
        )[0]

        let lastVisitedCategory = null
        try {
          const stored = localStorage.getItem(lastVisitedMainCategoryKey)
          if (stored) {
            lastVisitedCategory = navigationItems
              ?.filter(({ isMainCategory }) => isMainCategory)
              .find((item) => item.categoryId === stored)
          }
          // eslint-disable-next-line no-empty
        } catch {}

        const highlightedCategory = lastVisitedCategory
          ? lastVisitedCategory
          : activeCategory
          ? activeCategory
          : firstCategory

        if (highlightedCategory) {
          timeoutId = setTimeout(() => {
            handleClickCategoryItem(
              highlightedCategory.categoryId,
              highlightedCategory.children
            )
          }, 1)
        }
      }

      return () => {
        clearTimeout(timeoutId)
      }
    }, [showSlideMenu])

    const handleClickCategoryItem = (
      id: string,
      subCategories: NavigationItem[]
    ) => {
      setCurrentCategoryId(id)

      if (
        subCategories?.length > 0 &&
        subcategoriesEl.current &&
        slideMenu.current
      ) {
        subcategoriesEl.current.setSubcategories(id, subCategories)
      }
    }
    if (!showSlideMenu) {
      return null
    }

    const handleSubcategoryClick = (
      subcategory: NavigationItem,
      banners: HomeBanner[]
    ) => {
      setSelectedSubcategory(subcategory)
      setSelectedSubcategoryBanners(banners)
      if (subcategoriesItemsMenuEl.current) {
        subcategoriesItemsMenuEl.current.setSubcategories(
          subcategory.id,
          subcategory.children
        )
        slideMenu.current?.slideNext()
      }
    }

    if (!showSlideMenu) {
      return null
    }

    return (
      <MainSlideMenuPagesRedesign
        showSlideMenu={showSlideMenu}
        setShowSlideMenu={setShowSlideMenu}
        ref={slideMenu}
        navigationItems={navigationItems}
        onCategoryClick={handleClickCategoryItem}
        showHeaderPromo={showHeaderPromo}
        headerNotice={headerNotice}
        selectedSubcategory={selectedSubcategory}
        pages={[
          {
            page: (
              <MobileSubcategoriesMenuRedesign
                key={0}
                categoryId={navigationItems?.[0]?.id || ''}
                ref={subcategoriesEl}
                navigationItems={navigationItems?.[0]?.children || []}
                onSubcategoryClick={handleSubcategoryClick}
                otherMenuItems={otherMenuItems}
                currentCategoryId={currentCategoryId}
              />
            )
          },
          {
            page: (
              <MobileSubcategoryItemsMenuRedesign
                key={1}
                categoryUrl={selectedSubcategory?.url || ''}
                ref={subcategoriesItemsMenuEl}
                navigationItems={selectedSubcategory?.children || []}
                banners={selectedSubcategoryBanners}
                currentCategoryId={currentCategoryId}
              />
            )
          }
        ]}
      />
    )
  }
)

export default MainSlideMenuRedesign
